import { ArrowLeft, ArrowRight, Calendar, CalendarDate, ChevronLeft, ChevronLeftDouble, ChevronRight, ChevronRightDouble, Star06 } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { createPortal } from 'react-dom';
import ActionTooltip from 'src/components/ActionTooltip';
import ButtonCmp from 'src/components/ButtonCmp';
import ErrorMsg from 'src/components/errorMsg';
import { createDateWithoutTimezoneConversion } from 'src/utils/CommonFunctions';

import { IDateSelectProps } from './dateSelect.interface';

const renderCustomHeader = ({ date, changeYear, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }: any) => {
    const decreaseYear = () => changeYear(date.getFullYear() - 1);
    const increaseYear = () => changeYear(date.getFullYear() + 1);

    return (
        <div className="my-3 mx-[23px] flex justify-between items-center">
            <div className="flex gap-x-1">
                <ButtonCmp type="button" className="btn_secondary_black !py-1 !px-1" onClick={decreaseYear}>
                    <ChevronLeftDouble className="w-3.5 h-3.5" />
                </ButtonCmp>
                <ButtonCmp type="button" className="btn_secondary_black !py-1 !px-1" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                    <ChevronLeft className="w-3.5 h-3.5" />
                </ButtonCmp>
            </div>
            <span>
                {date.toLocaleString('default', { month: 'long' })} {date.getFullYear()}
            </span>
            <div className="flex gap-x-1">
                <ButtonCmp type="button" className="btn_secondary_black !py-1 !px-1" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                    <ChevronRight className="w-3.5 h-3.5" />
                </ButtonCmp>
                <ButtonCmp type="button" className="btn_secondary_black lg:!py-1 lg:!px-1" onClick={increaseYear}>
                    <ChevronRightDouble className="w-3.5 h-3.5" />
                </ButtonCmp>
            </div>
        </div>
    );
};

const DateSelect = ({
    inputName = '',
    placeholder,
    className = '',
    labelClassName = '',
    parentClassName,
    ReactDatePickerSelectClass,
    calendarIconClass,
    calendarIcon,
    errorText = null,
    isShowErrorMsg = true,
    required,
    label = '',
    tabIndex,
    isDisplayIcon = true,
    isShowCustomMonthYearHeader,
    dateSelectClassName,
    popperPlacement = 'bottom-end',
    onChangeFunc,
    isShowAiStar,
    dateSelectWithArrow,
    disabled,
    ReactDatePickerSelectWrapClass,
    id,
    selected,
    minDate,
    maxDate,
    dateFormat,
    value,
    isClearable,
    autoFocus,
    inline,
    showMonthYearPicker,
    size,
}: IDateSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const datePickerRef = useRef<DatePicker>(null);
    const customInputRef = useRef<HTMLDivElement>(null);
    const customCalendarRef = useRef<any>(null);

    selected = selected ? (typeof selected === 'string' ? selected?.split('T')[0] : `${selected.getFullYear()}-${selected.getMonth() + 1}-${selected.getDate()}`) : null;

    const CustomDateInput = React.forwardRef(({ onClick, onLeftArrowClick, onRightArrowClick, isLeftArrowDisabled, isRightArrowDisabled }: any, ref: any) => {
        const setRef = (element: HTMLDivElement | null) => {
            if (ref) {
                if (typeof ref === 'function') {
                    ref(element);
                } else if (ref.current !== undefined) {
                    (ref as any).current = element;
                }
            }

            if (customInputRef.current !== element) {
                (customInputRef as any).current = element;
            }
        };

        return (
            <div
                className={`truncate flex form_control text-grayLight900 font-medium [&.disabled]:border-borderPrimary [&.disabled]:bg-gray50 [&.disabled]:text-gray500 [&.disabled>svg]:hidden [&.disabled]:opacity-100
            ${dateSelectWithArrow ? 'justify-between' : !value ? 'pr-[25px]' : 'pr-[47px]'}
            ${size === 'sm' ? '!h-[28px] !py-1' : ''}
            ${isShowAiStar ? 'pl-[28px]' : ''}
            ${errorText ? 'border border-red-500' : ''}
            ${disabled ? 'cursor-not-allowed opacity-70' : ''}

            ${dateSelectClassName || ''}
          `}
                onClick={(e) => {
                    if (!disabled) {
                        onClick(e);
                    }
                }}
                ref={setRef}
            >
                {dateSelectWithArrow && (
                    <ArrowLeft
                        className={`w-4 h-4 transition-colors ${isLeftArrowDisabled || disabled ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}`}
                        onClick={(e) => {
                            e.stopPropagation();

                            if (onLeftArrowClick && !isLeftArrowDisabled && !disabled) {
                                onLeftArrowClick();
                            }
                        }}
                    />
                )}

                {/* Show placeholder when no value is selected */}
                <div className={dateSelectWithArrow ? 'flex justify-between items-center' : ''}>
                    {isShowAiStar && (
                        <div className={`w-4 h-4 mt-px ${dateSelectWithArrow ? 'mr-1' : 'absolute top-1/2 -translate-y-1/2 left-2 '}`}>
                            <ActionTooltip message={'Detected with AI'} isOpenChildTopPosOnTop={true} tooltipPosition="center" parentClassName="min-w-[120px]" openOnHover={true} type="light">
                                <Star06 className="w-4 h-4 text-primary " />
                            </ActionTooltip>
                        </div>
                    )}
                    {calendarIcon && <CalendarDate className="w-4 h-4 xl:mr-2 mr-1  " />}
                    <div dangerouslySetInnerHTML={{ __html: value || placeholder || '' }} className={`truncate ${!value ? 'text-gray-500' : ''}`} />
                </div>
                {dateSelectWithArrow && (
                    <ArrowRight
                        className={`w-4 h-4 transition-colors ${isRightArrowDisabled || disabled ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}`}
                        onClick={(e) => {
                            e.stopPropagation();

                            if (onRightArrowClick && !isRightArrowDisabled && !disabled) {
                                onRightArrowClick();
                            }
                        }}
                    />
                )}
            </div>
        );
    });

    CustomDateInput.displayName = 'CustomDateInput';

    const onChangeDate = (date: Date | [Date | null, Date | null] | Date[] | null, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        if (date && Array.isArray(date)) {
            date = date[0];
        }

        const normalizedDate = createDateWithoutTimezoneConversion(date as Date);

        let customEvent: any = event;
        customEvent = {
            event,
            ...{ value: normalizedDate, id: id, name: inputName },
        };
        onChangeFunc(customEvent);
    };

    // Check if arrows should be disabled
    const isLeftArrowDisabled = (() => {
        if (!selected || !minDate) return false;

        const previousDay = new Date(selected);
        previousDay.setDate(previousDay.getDate() - 1);
        const normalizedPreviousDay = createDateWithoutTimezoneConversion(previousDay);
        const normalizedMinDate = createDateWithoutTimezoneConversion(minDate);

        return normalizedPreviousDay && normalizedMinDate ? normalizedPreviousDay < normalizedMinDate : false;
    })();

    const isRightArrowDisabled = (() => {
        if (!selected || !maxDate) return false;

        const nextDay = new Date(selected);
        nextDay.setDate(nextDay.getDate() + 1);
        const normalizedNextDay = createDateWithoutTimezoneConversion(nextDay);
        const normalizedMaxDate = createDateWithoutTimezoneConversion(maxDate);

        return normalizedNextDay && normalizedMaxDate ? normalizedNextDay > normalizedMaxDate : false;
    })();

    const handlePreviousDay = () => {
        if (selected) {
            // Create a new date for the previous day
            const previousDay = new Date(selected);
            previousDay.setDate(previousDay.getDate() - 1);

            // Normalize to midnight using our helper function
            const normalizedPreviousDay = createDateWithoutTimezoneConversion(previousDay);

            // Check if the previous day is before minDate
            if (minDate && normalizedPreviousDay) {
                const normalizedMinDate = createDateWithoutTimezoneConversion(minDate);

                if (normalizedMinDate && normalizedPreviousDay < normalizedMinDate) {
                    return;
                }
            }

            const customEvent: any = {
                value: normalizedPreviousDay,
                id: id,
                name: inputName,
            };
            setIsOpen(false);
            onChangeFunc(customEvent);
        }
    };

    const handleNextDay = () => {
        if (selected) {
            // Create a new date for the next day
            const nextDay = new Date(selected);
            nextDay.setDate(nextDay.getDate() + 1);

            // Normalize to midnight using our helper function
            const normalizedNextDay = createDateWithoutTimezoneConversion(nextDay);

            // Check if the next day is after maxDate
            if (maxDate && normalizedNextDay) {
                const normalizedMaxDate = createDateWithoutTimezoneConversion(maxDate);

                if (normalizedMaxDate && normalizedNextDay > normalizedMaxDate) {
                    return;
                }
            }

            const customEvent: any = {
                value: normalizedNextDay,
                id: id,
                name: inputName,
            };
            setIsOpen(false);
            onChangeFunc(customEvent);
        }
    };

    return (
        <div className={`${parentClassName} ${isShowAiStar ? 'ai-gradient-border-wrap' : ''}`}>
            <div className="flex">
                {label && (
                    <label className={`form_label mb-1.5 ${labelClassName}`}>
                        {label}
                        {required && <span className="text-red-600 leading-4 text-xs ml-0.5">*</span>}
                    </label>
                )}
            </div>
            <div className={`relative w-full cursor-pointer wal-single-date-picker ${ReactDatePickerSelectWrapClass}`}>
                <DatePicker
                    ref={datePickerRef}
                    placeholderText={placeholder}
                    popperContainer={({ children }) => createPortal(<div className={`one-month-datepicker wal-single-date-picker ${ReactDatePickerSelectClass}`}>{children}</div>, document.body)}
                    open={isOpen}
                    onClickOutside={(event) => {
                        setTimeout(() => {
                            if (customInputRef.current && !customInputRef.current.contains(event.target as Node)) {
                                setIsOpen(false);
                            }

                            if (customCalendarRef.current && !customCalendarRef.current.contains(event.target as Node)) {
                                setIsOpen(false);
                            }
                        }, 100);
                    }}
                    onInputClick={() => {
                        if (!disabled) {
                            setIsOpen((prev) => !prev);
                        }
                    }}
                    onChange={(date) => {
                        if (!disabled) {
                            onChangeDate(date);
                            setIsOpen(false);
                        }
                    }}
                    onSelect={(date) => {
                        if (!disabled && date) {
                            const today = createDateWithoutTimezoneConversion(new Date());
                            const clickedDate = createDateWithoutTimezoneConversion(date);

                            // Convert selected string to Date for comparison
                            const currentSelectedDate = selected ? createDateWithoutTimezoneConversion(new Date(selected ?? '')) : null;

                            if (
                                (today && clickedDate && clickedDate.getTime() === today.getTime()) ||
                                (clickedDate && currentSelectedDate && clickedDate.getTime() === currentSelectedDate.getTime())
                            ) {
                                setIsOpen(false);
                            }
                        }
                    }}
                    selected={selected ? moment(selected).toDate() : undefined}
                    className={`${className} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    tabIndex={tabIndex}
                    showFourColumnMonthYearPicker={true}
                    renderCustomHeader={isShowCustomMonthYearHeader ? renderCustomHeader : undefined}
                    id={id}
                    onKeyDown={(e) => {
                        if (disabled) {
                            e.preventDefault();

                            return;
                        }

                        if (e?.keyCode === 9 || e?.which === 9 || e?.keyCode === 27) {
                            setIsOpen(false);
                        }
                    }}
                    fixedHeight={true}
                    autoComplete="off"
                    name={inputName}
                    dropdownMode="select"
                    showIcon={isDisplayIcon}
                    icon={
                        isDisplayIcon ? (
                            // No ref prop here: Calendar is a plain function component from
                            // @untitled-ui/icons-react (no forwardRef), so React drops the ref and
                            // warns. customCalendarRef is populated from e.target in onClick below,
                            // which is what onClickOutside actually reads.
                            <Calendar
                                className={`h-5 w-5 ${disabled ? 'text-gray500' : 'text-textSecondary'}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    customCalendarRef.current = e.target;

                                    if (!disabled) {
                                        setIsOpen((prev) => !prev);
                                    }
                                }}
                            />
                        ) : null
                    }
                    toggleCalendarOnIconClick={!disabled}
                    calendarIconClassName={`icon absolute right-2 top-1/2 -translate-y-1/2 text-lg ${disabled ? 'text-gray500 pointer-events-none' : 'text-textSecondary'} ${calendarIconClass}`}
                    customInput={
                        <CustomDateInput
                            value={selected ? moment(selected).toDate() : ''}
                            placeholder={placeholder}
                            dateSelectClassName={dateSelectClassName}
                            isShowAiStar={isShowAiStar}
                            dateSelectWithArrow={dateSelectWithArrow}
                            calendarIcon={calendarIcon}
                            errorText={!!errorText}
                            onLeftArrowClick={handlePreviousDay}
                            onRightArrowClick={handleNextDay}
                            isLeftArrowDisabled={isLeftArrowDisabled}
                            isRightArrowDisabled={isRightArrowDisabled}
                        />
                    }
                    popperPlacement={popperPlacement}
                    disabled={disabled}
                    minDate={minDate}
                    maxDate={maxDate}
                    dateFormat={dateFormat}
                    value={value}
                    isClearable={isClearable}
                    autoFocus={autoFocus}
                    inline={inline}
                    showMonthYearPicker={showMonthYearPicker}
                />
            </div>
            {isShowErrorMsg && errorText && <ErrorMsg errorText={errorText} />}
        </div>
    );
};

export default DateSelect;
